import { Component, OnInit } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";

import { getAccountName } from "../../util";
import { NavigationService } from "../../common/navigation/navigation.service";
import { AccountInfoService } from "../../common/account-info/account-info-service"; // TODO rm once mfa feature is enabled for all accounts
import { QRCodeResult, AccountInfo } from "../../models";
import { ValidationService } from "../../common/validation/validation.service";
import { MFA_SETUP_STATE } from "./models";
import { MfaSettingsService } from "../../common/authentication/mfa-settings/mfa-settings.service";
import { SnackbarService } from "../../common/snackbar/snackbar.service";
import { TranslateService } from "../../common/translate";
import { BrowserTitleService } from "../../common/browser-title/browser-title.service";
import { AuthenticationService } from "../../common/authentication/authentication.service";

@Component({
	selector: "sdp-mfa",
	templateUrl: "./mfa.component.html"
})

export class MfaComponent implements OnInit {
	public accountName = getAccountName();
	public accountInfo: AccountInfo | undefined;
	public errMessage = "";
	public mfaSetupData: QRCodeResult = { result: false, message: "", secretCode: "", dataURL: "", tfaURL: "" };
	public totpForm: FormGroup;
	public mfaDeviceName = "";
	public isDataLoaded = false;
	public authenticatorName: string;

	public readonly MFA_DEVICE_NAME = "SafeConnect_SDP";
	public readonly MFA_SETUP_STATE = MFA_SETUP_STATE;

	public state: MFA_SETUP_STATE = MFA_SETUP_STATE.OFF;
	private prevState: MFA_SETUP_STATE = this.state;

	constructor(
		private formBuilder: FormBuilder,
		private navigationService: NavigationService,
		private validationService: ValidationService,
		private snackbarService: SnackbarService,
		private translateService: TranslateService,
		private accountInfoService: AccountInfoService, // TODO rm once mfa feature is enabled for all accounts
		private mfaSettingsService: MfaSettingsService,
		private titleService: BrowserTitleService,
		private authService: AuthenticationService
	) {
		this.authenticatorName = `${window.location.hostname} (${this.authService.getCurrentUser()!.email})`;
		this.totpForm = this.formBuilder.group({
			totpCode: ["", Validators.compose([Validators.required, this.validationService.totpCode])],
		});
	}

	public async ngOnInit() {
		this.titleService.setTitle("settings.title");

		this.accountInfo = await this.accountInfoService.getAccountInfo(); // TODO rm once mfa feature is enabled for all accounts

		this.isDataLoaded = false;
		try {
			const mfaDevice = await this.mfaSettingsService.isMfaEnabled();
			if (!mfaDevice) {
				throw new Error("Unable to get MFA Settings");
			}
			if (mfaDevice.enabled) {
				this.state = MFA_SETUP_STATE.ON;
				this.mfaDeviceName = !!mfaDevice.name ? mfaDevice.name : "";
			} else {
				this.state = MFA_SETUP_STATE.OFF;
			}
			this.isDataLoaded = true;
		} catch (e) {
			this.state = MFA_SETUP_STATE.OFF;
			this.isDataLoaded = true;
		}
	}

	public showDeviceLabelInput() {
		this.prevState = this.state;
		this.state = MFA_SETUP_STATE.INITIATED;
	}

	public async requestMfaKey() {
		// default devicename for verifying tokens with AWS
		this.isDataLoaded = false;
		this.mfaSettingsService.initiateMfaSetup(this.MFA_DEVICE_NAME).then((result) => {
			if (!result.result) {
				this.errMessage = "error.environment";
			} else {
				this.errMessage = "";
				this.state = MFA_SETUP_STATE.WAITING_FOR_TOTP; // triggers ui change
				this.mfaSetupData = result;
			}
			this.isDataLoaded = true;
		}).catch((err) => {
			this.isDataLoaded = true;
			this.errMessage = "error.environment";
		});
	}

	public async completeMfaSetup() {
		if (this.totpForm.valid) {
			this.isDataLoaded = false;
			const confirmPromise = this.mfaSettingsService.completeMfaSetup(
				this.totpForm.value["totpCode"],
				this.MFA_DEVICE_NAME
			);
			confirmPromise.then(() => {
				this.errMessage = "";
				this.state = MFA_SETUP_STATE.ON;
				this.mfaDeviceName = this.MFA_DEVICE_NAME;
				this.snackbarService.open(this.translateService.translate("settings.mfa.enabled"));
				this.isDataLoaded = true;
			}).catch((e) => {
				if (!!e && !!e.message && e.message.includes("mismatch")) {
					this.errMessage = "error.totp.codeMismatch";
				} else {
					this.errMessage = "error.environment";
				}
				this.isDataLoaded = true;
			});
		}
	}

	public async cancelAction() {
		this.totpForm.reset();
		this.state = this.prevState;
	}

	public async disableMfa() {
		this.isDataLoaded = false;
		await this.mfaSettingsService.disableMfa();
		this.errMessage = "";
		this.mfaSetupData = { result: false, message: "", secretCode: "", dataURL: "", tfaURL: "" };
		this.state = MFA_SETUP_STATE.OFF;
		this.snackbarService.open(this.translateService.translate("settings.mfa.disabled"));
		this.isDataLoaded = true;
	}

	public goTo(loc: string) {
		this.navigationService.navigate([`/${loc}`]);
	}

}
